import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { idsQueryParamsBuilder } from '@shared/helpers';
import { PersonalParamDto } from '@shared/interfaces/dtos/params';
import { PointsQuery } from '@shared/interfaces/points';
import { BusinessRouter } from '@shared/models/dev';
import { BaseHttpService } from '@shared/services/base-http.service';
import { PersonalParamsService } from '@shared/services/personal-params.service';
import { PointsAdvancedSearchQuery } from '@shared/types/points';
import { ScheduleUtil } from '@shared/utils';
import { isEmpty } from 'lodash';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PointsAdvancedSearchService extends BaseHttpService {
  protected endPoint: string = '';

  private readonly businessRouter = inject(BusinessRouter);
  private readonly personalParamsService = inject(PersonalParamsService);

  private readonly _currentSearch = signal<PointsQuery | null>(null);
  public readonly currentSearch = this._currentSearch.asReadonly();
  public readonly hasActiveSearch = computed(() => {
    const currentSearch = this.currentSearch();

    return currentSearch && !isEmpty(currentSearch);
  });

  public readonly shouldPopulateForms = signal<boolean>(false);
  public readonly formPopulationProgress = signal({
    generalData: true,
    meeting: true,
    moreData: true,
    agents: true,
  });
  private readonly formPopulationProgressEffect = effect(
    () => {
      const progress = this.formPopulationProgress();
      const allDone = Object.values(progress).every((done) => done === true);

      if (allDone) this.shouldPopulateForms.set(false);
    },
    { allowSignalWrites: true }
  );

  public setFormPopulationProgressAsDoneFor(section: 'generalData' | 'meeting' | 'moreData' | 'agents'): void {
    this.formPopulationProgress.update((current) => ({
      ...current,
      [section]: true,
    }));
  }
  public setFormPopulationProgressAsStartedFor(section: 'generalData' | 'meeting' | 'moreData' | 'agents'): void {
    this.formPopulationProgress.update((current) => ({
      ...current,
      [section]: false,
    }));
  }

  public setCurrentSearch(search: PointsQuery): void {
    this._currentSearch.set(search);
  }

  public buildQueryFromAdvancedSearch(value: PointsAdvancedSearchQuery): PointsQuery {
    const { generalData, meeting, moreData, agents } = value;
    this.logger.log(value, 'Points Advanced Search Service');

    return {
      ExclureNonInclus: generalData?.excludeNotIncludedPoints ?? undefined,
      InclureAnnuler: generalData?.includeCancelPoints ?? undefined,
      ACorriger: generalData?.myPointsToCorrect ?? undefined,
      CheckContenuRTF: generalData?.searchIntoContent ?? undefined,
      NeverReadOnly: generalData?.onlyUnreads ?? undefined,
      RechercheGlobale: generalData?.keyword,
      TypeIds: idsQueryParamsBuilder((generalData.pointTypes ?? []).map((type) => type.id)),
      StatutIds: idsQueryParamsBuilder((generalData.pointStatuses ?? []).map((status) => status.id)),
      ServiceIds: idsQueryParamsBuilder(
        (generalData.services ?? []).flatMap((node) => (node?.data ? [node.data.id] : []))
      ),
      MatiereIds: idsQueryParamsBuilder((generalData.matters ?? []).map((matter) => matter.id)),
      TypeSeanceIds: idsQueryParamsBuilder((meeting.meetingTypes ?? []).map((type) => type.id)),
      SeanceIds: idsQueryParamsBuilder((meeting.meetings ?? []).map((meeting) => meeting.id)),
      DateDebut: meeting.date instanceof Array ? ScheduleUtil.toHttpRequestFormat(meeting.date[0]) : undefined,
      DateFin: meeting.date instanceof Array ? ScheduleUtil.toHttpRequestFormat(meeting.date[1]) : undefined,
      Date:
        meeting.date instanceof Array
          ? undefined
          : meeting.date
            ? ScheduleUtil.toHttpRequestFormat(meeting.date)
            : undefined,
      TypeDecisionIds: idsQueryParamsBuilder((moreData.decisionTypes ?? []).map((type) => type.id)),
      EchevinatIds: idsQueryParamsBuilder((moreData.aldermens ?? []).map((alderman) => alderman.id)),
      ClassementIds: idsQueryParamsBuilder((moreData.classifications ?? []).map((classification) => classification.id)),
      DossierClassementIds: moreData.sheets?.length
        ? idsQueryParamsBuilder(moreData.sheets?.map((sheet) => sheet.id))
        : idsQueryParamsBuilder(moreData.folders?.map((folder) => folder.id)),

      CreeParContactId: idsQueryParamsBuilder(agents.users?.map((user) => user.contactForUser.contactId)),
      AgentTraitantIdiAdmins: idsQueryParamsBuilder(agents.agents?.map((user) => user.contactForUser.contactId)),
    };
  }

  public saveSearch(
    search: PointsAdvancedSearchQuery,
    options: {
      name: string;
      shownInDashboard: boolean;
      order?: number;
      //Tuple of [classificationCodeId, folderId, sheetId]
      classification?: [number, number, number];
    }
  ): Observable<unknown> {
    const { name, shownInDashboard = true, order, classification } = options;

    return this.personalParamsService.insertNewPersonalParam({
      scope: 'savedSearches',
      body: {
        description: options.name,
        hideInDashboard: !shownInDashboard,
        displayOrder: order ?? 0,
        value: JSON.stringify({ ...this.buildQueryFromAdvancedSearch(search), classification }),
        shared: true,
        name: name,
      },
    });
  }

  public emptyCurrentSearch(): void {
    this._currentSearch.set(null);
  }

  public search(search: PointsAdvancedSearchQuery): void {
    this._currentSearch.set(this.buildQueryFromAdvancedSearch(search));
    this.businessRouter.goToPointsList();
  }

  public searchFromASavedSearch(search: PersonalParamDto): void {
    const searchValue: PointsQuery = JSON.parse(search.value);
    this._currentSearch.set(searchValue);
    this.businessRouter.goToPointsList();
  }
}
